import { useRef, useState } from 'react';
import { Clipboard, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SmartTagInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
  bulkPasteEnabled?: boolean;
  monospace?: boolean;
  accent?: 'neutral' | 'warning';
  hint?: string;
}

export function SmartTagInput({
  value = [],
  onChange,
  placeholder = 'Type and press Enter…',
  suggestions = [],
  bulkPasteEnabled = true,
  monospace = false,
  accent = 'neutral',
  hint,
}: SmartTagInputProps) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const chipBase = accent === 'warning'
    ? 'bg-[hsl(var(--status-error-bg))] text-[hsl(var(--status-error))] border-[hsl(var(--status-error)/0.3)]'
    : 'bg-warm-bg text-foreground border-border';
  const removeColor = accent === 'warning'
    ? 'text-[hsl(var(--status-error)/0.7)] hover:text-[hsl(var(--status-error))]'
    : 'text-muted-foreground hover:text-foreground';

  const add = (raw: string) => {
    const items = String(raw).split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    const next = [...value];
    items.forEach((it) => {
      if (!next.includes(it)) next.push(it);
    });
    onChange(next);
    setDraft('');
  };
  const remove = (i: number) => {
    const n = [...value];
    n.splice(i, 1);
    onChange(n);
  };
  const updateAt = (i: number, v: string) => {
    if (!v.trim()) {
      remove(i);
      return;
    }
    const n = [...value];
    n[i] = v.trim();
    onChange(n);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (draft.trim()) add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) {
      remove(value.length - 1);
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!bulkPasteEnabled) return;
    const text = e.clipboardData.getData('text');
    const lines = text.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (lines.length > 1) {
      e.preventDefault();
      setBulkText(text);
      setBulkOpen(true);
    }
  };

  const handleBulkAdd = () => {
    const lines = bulkText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    const next = [...value];
    lines.forEach((l) => {
      if (!next.includes(l)) next.push(l);
    });
    onChange(next);
    setBulkOpen(false);
    setBulkText('');
  };

  const remaining = suggestions.filter((s) => !value.includes(s));
  const newCount = bulkText.split(/[\n,]/).map((s) => s.trim()).filter((s) => s && !value.includes(s)).length;

  return (
    <div>
      <div
        onClick={() => inputRef.current?.focus()}
        className={cn(
          'flex flex-wrap gap-1.5 items-center px-2 py-1.5 min-h-[42px]',
          'rounded-lg bg-card border transition-[border-color,box-shadow] duration-150 cursor-text',
          focused
            ? 'border-coral ring-[3px] ring-coral/15'
            : 'border-border',
        )}
      >
        {value.map((v, i) =>
          editIdx === i ? (
            <input
              key={`e${i}`}
              autoFocus
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onBlur={() => {
                updateAt(i, editText);
                setEditIdx(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  updateAt(i, editText);
                  setEditIdx(null);
                }
                if (e.key === 'Escape') setEditIdx(null);
              }}
              className={cn(
                'border border-coral outline-none rounded-md px-2 py-0.5 text-[12.5px] font-medium bg-card min-w-[60px]',
                monospace && 'font-mono',
              )}
            />
          ) : (
            <span
              key={v + i}
              onClick={(e) => {
                e.stopPropagation();
                setEditIdx(i);
                setEditText(v);
              }}
              title="Click to edit"
              className={cn(
                'inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-md text-[12.5px] font-medium border cursor-pointer',
                chipBase,
                monospace && 'font-mono',
              )}
            >
              {v}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(i);
                }}
                className={cn(
                  'grid place-items-center w-4 h-4 rounded bg-transparent border-none cursor-pointer',
                  removeColor,
                )}
              >
                <X className="size-3" />
              </button>
            </span>
          ),
        )}
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onPaste={onPaste}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            if (draft.trim()) add(draft);
          }}
          placeholder={value.length === 0 ? placeholder : ''}
          className={cn(
            'flex-1 min-w-[120px] border-0 outline-none px-1 py-1 text-[13.5px] bg-transparent',
            monospace && 'font-mono',
          )}
        />
      </div>

      <div className="flex items-center justify-between mt-1.5 gap-2.5 flex-wrap">
        <div className="flex flex-wrap gap-1 items-center flex-1 min-w-0">
          {remaining.length > 0 ? (
            <>
              <span className="text-[11px] text-muted-foreground mr-1">Try:</span>
              {remaining.slice(0, 5).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => add(s)}
                  className="text-[11.5px] px-1.5 py-0.5 rounded bg-transparent border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-coral cursor-pointer transition-colors"
                >
                  + {s}
                </button>
              ))}
            </>
          ) : (
            <span className="text-[11px] text-muted-foreground">
              {hint || `${value.length} item${value.length !== 1 ? 's' : ''} · click a chip to edit`}
            </span>
          )}
        </div>
        {bulkPasteEnabled && (
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-transparent border border-border text-muted-foreground hover:text-foreground cursor-pointer font-medium"
          >
            <Clipboard className="size-3" /> Bulk
          </button>
        )}
      </div>

      {bulkOpen && (
        <div
          onClick={() => setBulkOpen(false)}
          className="fixed inset-0 bg-foreground/30 backdrop-blur-sm grid place-items-center z-[80]"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-[460px] max-w-[92vw] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="px-4 pt-3.5 pb-2.5 border-b border-border">
              <div className="text-sm font-bold">Bulk add</div>
              <div className="text-[11.5px] text-muted-foreground mt-0.5">
                One per line, or comma-separated. Duplicates skipped.
              </div>
            </div>
            <div className="p-3.5">
              <textarea
                autoFocus
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={'Item 1\nItem 2\nItem 3'}
                rows={8}
                className={cn(
                  'w-full px-3 py-2.5 text-[13px] border border-border rounded-lg outline-none bg-card resize-y min-h-[140px] focus:border-coral focus:ring-[3px] focus:ring-coral/15',
                  monospace && 'font-mono',
                )}
              />
            </div>
            <div className="px-3.5 py-2.5 border-t border-border flex justify-between items-center gap-2">
              <div className="text-[11.5px] text-muted-foreground">{newCount} new</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setBulkOpen(false)}
                  className="px-3 py-1.5 rounded-md text-[12.5px] font-medium bg-card border border-border text-foreground hover:bg-warm-bg cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkAdd}
                  className="px-3 py-1.5 rounded-md text-[12.5px] font-semibold bg-coral hover:bg-coral-hover border border-coral text-white cursor-pointer"
                >
                  Add all
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
